import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const DashboardBuilder = ({ projectId, userProfile }) => {
  const [dashboardName, setDashboardName] = useState('');
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [layout, setLayout] = useState('grid');

  const availableWidgets = [
    { id: 'kpi-leads', name: 'Total Leads', icon: 'Users', category: 'KPI' },
    { id: 'kpi-value', name: 'Valor Total', icon: 'DollarSign', category: 'KPI' },
    { id: 'kpi-conversion', name: 'Tasa de Conversión', icon: 'TrendingUp', category: 'KPI' },
    { id: 'chart-funnel', name: 'Embudo de Conversión', icon: 'Filter', category: 'Gráfico' },
    { id: 'chart-source', name: 'Leads por Fuente', icon: 'Share2', category: 'Gráfico' },
    { id: 'chart-timeline', name: 'Línea de Tiempo', icon: 'Activity', category: 'Gráfico' },
    { id: 'table-advisors', name: 'Rendimiento Asesores', icon: 'Award', category: 'Tabla' },
    { id: 'table-campaigns', name: 'ROI Campañas', icon: 'Target', category: 'Tabla' }
  ];

  const toggleWidget = (widgetId) => {
    if (selectedWidgets?.includes(widgetId)) {
      setSelectedWidgets(selectedWidgets?.filter(id => id !== widgetId));
    } else {
      setSelectedWidgets([...selectedWidgets, widgetId]);
    }
  };

  const handleSaveDashboard = () => {
    if (!dashboardName) {
      toast?.error('Por favor ingresa un nombre para el dashboard');
      return;
    }
    if (selectedWidgets?.length === 0) {
      toast?.error('Por favor selecciona al menos un widget');
      return;
    }
    toast?.success('Dashboard guardado exitosamente');
    // TODO: Save to database
  };

  const groupedWidgets = availableWidgets?.reduce((acc, widget) => {
    if (!acc?.[widget?.category]) {
      acc[widget?.category] = [];
    }
    acc?.[widget?.category]?.push(widget);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Dashboard Settings */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Settings" size={20} className="mr-2 text-primary" />
          Configuración del Dashboard
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Nombre del Dashboard
            </label>
            <input
              type="text"
              value={dashboardName}
              onChange={(e) => setDashboardName(e?.target?.value)}
              placeholder="Ej: Dashboard Ejecutivo"
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Diseño
            </label>
            <select
              value={layout}
              onChange={(e) => setLayout(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="grid">Cuadrícula</option>
              <option value="list">Lista</option>
              <option value="masonry">Mosaico</option>
            </select>
          </div>
        </div>
      </div>
      {/* Widget Library */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Layers" size={20} className="mr-2 text-primary" />
          Biblioteca de Widgets
        </h3>
        <div className="space-y-6">
          {Object.entries(groupedWidgets)?.map(([category, widgets]) => (
            <div key={category}>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">{category}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {widgets?.map(widget => (
                  <button
                    key={widget?.id}
                    onClick={() => toggleWidget(widget?.id)}
                    className={`p-4 rounded-lg border-2 transition-smooth text-left ${
                      selectedWidgets?.includes(widget?.id)
                        ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Icon name={widget?.icon} size={20} className="text-primary" />
                      {selectedWidgets?.includes(widget?.id) && (
                        <Icon name="CheckCircle" size={16} className="text-primary" />
                      )}
                    </div>
                    <div className="text-sm font-medium text-foreground">{widget?.name}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Preview */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Eye" size={20} className="mr-2 text-primary" />
          Vista Previa
        </h3>
        {selectedWidgets?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="LayoutGrid" size={48} className="mx-auto mb-4 opacity-50" />
            <p>Selecciona widgets para ver la vista previa</p>
          </div>
        ) : (
          <div className={`grid gap-4 ${
            layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
            layout === 'list'? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'
          }`}>
            {selectedWidgets?.map(widgetId => {
              const widget = availableWidgets?.find(w => w?.id === widgetId);
              return (
                <div key={widgetId} className="bg-muted/30 rounded-lg p-6 border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Icon name={widget?.icon} size={20} className="mr-2 text-primary" />
                      <span className="text-sm font-medium text-foreground">{widget?.name}</span>
                    </div>
                    <button
                      onClick={() => toggleWidget(widgetId)}
                      className="text-muted-foreground hover:text-error"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                  <div className="h-32 bg-background rounded flex items-center justify-center text-muted-foreground text-sm">
                    Widget Preview
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={() => {
            setDashboardName('');
            setSelectedWidgets([]);
          }}
          className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth"
        >
          Limpiar
        </button>
        <button
          onClick={handleSaveDashboard}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth flex items-center"
        >
          <Icon name="Save" size={16} className="mr-2" />
          Guardar Dashboard
        </button>
      </div>
    </div>
  );
};

export default DashboardBuilder;